import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { formatMeasure } from '../../utils/measure';

const changeColor = (change) => {
  const numeric = parseFloat(String(change).replace(/[^0-9.+-]/g, ''));
  if (!Number.isFinite(numeric) || numeric === 0) return 'text.secondary';
  return numeric > 0 ? 'success.main' : 'error.main';
};

const validItems = (items) => (Array.isArray(items) ? items : []).filter((item) => (
  item && typeof item.label === 'string' && Number.isFinite(Number(item.value))
));

/** One figure as a row: the label on the left (wrapping), the value on the right (never wrapping). */
const KpiRow = ({ item }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      gap: 1.5,
      py: 0.75,
      borderBottom: '1px solid',
      borderColor: 'divider',
      '&:last-of-type': { borderBottom: 'none' },
    }}
  >
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0, flex: 1 }}>
      {item.label}
    </Typography>
    <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ whiteSpace: 'nowrap', lineHeight: 1.3 }}>
        {formatMeasure(item.value, item.unit)}
      </Typography>
      {item.change && (
        <Typography variant="caption" sx={{ color: changeColor(item.change), fontWeight: 600, whiteSpace: 'nowrap' }}>
          {item.change}
        </Typography>
      )}
    </Box>
  </Box>
);

/** One figure as a tile for the wide chat: label (up to two lines), value, change note. */
const KpiTile = ({ item }) => (
  <Box sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', minWidth: 0 }}>
    <Typography
      variant="caption"
      color="text.secondary"
      component="div"
      title={item.label}
      sx={{
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        lineHeight: 1.3,
        minHeight: '2.6em',
      }}
    >
      {item.label}
    </Typography>
    <Typography variant="h6" fontWeight="bold" sx={{ my: 0.25, whiteSpace: 'nowrap' }}>
      {formatMeasure(item.value, item.unit)}
    </Typography>
    {item.change && (
      <Typography variant="caption" sx={{ color: changeColor(item.change), fontWeight: 600 }}>
        {item.change}
      </Typography>
    )}
  </Box>
);

/**
 * Key figures from the assistant, rendered for a `kpi` visualization. The layout follows the
 * container, not the viewport: rows in the narrow chat panel, tiles that fill the width in the
 * full chat.
 */
const KpiTiles = ({ title, description, items = [], isPanel = false }) => {
  const theme = useTheme();
  const figures = validItems(items);
  if (figures.length === 0) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 1.5,
        p: isPanel ? 1.5 : 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: theme.palette.modern?.slate?.[50] || theme.palette.background.paper,
      }}
    >
      {title && (
        <Typography sx={{ fontWeight: 700, fontSize: isPanel ? '0.95rem' : '1.05rem', mb: 0.25 }}>
          {title}
        </Typography>
      )}
      {description && (
        <Typography sx={{ color: 'text.secondary', fontSize: isPanel ? '0.8rem' : '0.875rem' }}>
          {description}
        </Typography>
      )}
      {isPanel ? (
        <Box sx={{ mt: 0.5 }}>
          {figures.map((item, index) => (
            <KpiRow key={`${item.label}-${index}`} item={item} />
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
            gap: 1.5,
            mt: 1,
          }}
        >
          {figures.map((item, index) => (
            <KpiTile key={`${item.label}-${index}`} item={item} />
          ))}
        </Box>
      )}
    </Paper>
  );
};

export default KpiTiles;
